import type { LLMTabContentForDispatch } from '../CustomDash/components/AIAssistant/llm-tab-content-dispatch';

/**
 * Canned BREAKDOWN configs for `get_data_breakdown` — the salient-slice
 * sibling of `direct-answer-configs.ts`. Where a direct answer returns ONE
 * number, a breakdown answer returns a RANKED set (digest `topCategories` +
 * `bottomCategories`) and the model cites the end(s) that answer the
 * question: worst for leaving/dissatisfaction, best for loved/strengths,
 * both plus companions for judgement asks.
 *
 * ADDING A BREAKDOWN (mirrors the direct-answer recipe):
 * 1. Verify the shape HEADLESS first (config-safe ≠ data-safe — postings,
 *    2026-07-04): run a real drawer turn to capture
 *    `window.__gaLastHeadlessInput`, then re-fire
 *    `window.__gaFetchHeadlessDigest` with the candidate item. Console-built
 *    probe items MUST carry an `id`: `usePlotMetric` resolves the metric via
 *    `getDefaultMetricOverrides`, which returns undefined for id-less items,
 *    and the provider then renders ChartNotSupported without ever settling —
 *    a silent 30s timeout, not an error. (These dispatch configs are safe:
 *    `getValidatedPositionedItemsFromLlmContent` assigns ids — the dispatch
 *    item type has no id field.) Expect `topCategories` populated;
 *    `bottomCategories` needs >5 rows.
 * 2. Add the entry below + `BREAKDOWN_ANSWER_COMPANIONS` (companions are
 *    DIRECT_ANSWER_CONFIGS metrics, fetched in parallel exactly as in
 *    get_data_answer).
 * 3. Add the breakdown value to the dash-meta registry's
 *    `get_data_breakdown` enum + one line to its description, and teach the
 *    loop prompt which question shapes it answers.
 *
 * PHASE-1 SCOPE: `sentiment_aspects` only. Phase-2 candidates (each needs
 * its own step-1 shape probe): transition_outflow_destinations /
 * transition_inflow_sources (`transitions_inflows_and_outflows` is in
 * DIVERGING_BAR_METRICS), postings_top_roles (PostingTopQuery via
 * secondaryDimension), headcount_by_geography/role/seniority/gender,
 * pay_by_role. See
 * docs/superpowers/plans/2026-07-08-breakdown-grounded-answers.md.
 *
 * `diverging-bar` (NOT `-combo`): the plain variant's provider branch emits a
 * flat labeled SINGLE set the digest flattens today; the combo variant emits
 * the nested BREAKDOWN_SINGLE shape the digest cannot descend (probed empty,
 * 2026-07-08: plain variant = 20 aspect rows in 813ms with a real artifact).
 */

const BREAKDOWN_GRID = { rows: 36, columns: 48 } as const;
/** Full-width single widget; height is cosmetic (no card ever renders). */
const BREAKDOWN_POSITION = { x: 0, y: 0, width: 48, height: 22 } as const;

function breakdownConfig(
  metric: string,
  chartType: string,
  title: string
): LLMTabContentForDispatch {
  return {
    items: [
      {
        title,
        position: BREAKDOWN_POSITION,
        plot: {
          chartType,
          defaultMetricOptions: [metric],
        },
      },
    ],
    grid: BREAKDOWN_GRID,
    secondaryFilters: [],
  };
}

export type BreakdownAnswerKey = 'sentiment_aspects';

export const BREAKDOWN_ANSWER_CONFIGS: Record<
  BreakdownAnswerKey,
  {
    config: LLMTabContentForDispatch;
    /** Standard-page domain for the offer:page wiring. */
    domain: string;
    unitHint: string;
    /** How the tool result frames each ranked end for THIS breakdown. */
    highEndLabel: string;
    lowEndLabel: string;
  }
> = {
  sentiment_aspects: {
    config: breakdownConfig(
      'sentiment_top_bottom',
      'diverging-bar',
      'Sentiment Topics'
    ),
    domain: 'sentiment',
    unitHint:
      'aspect scores normalized around the peer median — positive = above typical peers, negative = below (the attrition-factor side); cite them as scores (e.g. "0.31"), never as percentages, and never invent a scale',
    highEndLabel: 'best-rated aspects (hiring-factor side)',
    lowEndLabel: 'worst-rated aspects (attrition-factor side)',
  },
};

/**
 * Companion single-value metrics fetched alongside a breakdown for outcome
 * context (each is a `DIRECT_ANSWER_CONFIGS` key — same fixed ≤2 budget and
 * fetch path as `DIRECT_ANSWER_COMPANIONS`). For sentiment_aspects the
 * companions carry the OUTCOME (attrition) and the overall score, so one
 * get_data_breakdown round grounds a full "why are people leaving" answer.
 */
export const BREAKDOWN_ANSWER_COMPANIONS: Record<BreakdownAnswerKey, string[]> =
  {
    sentiment_aspects: ['attrition_rate', 'overall'],
  };
